import { getDefaultStore } from 'jotai'

import { mapUnloRoute } from '@dfds-pax/utils'

import { getBasketEnriched } from '@/app/[locale]/[ferry]/_actions/getBasketEnriched'
import { SelectedPet } from '@/app/[locale]/[ferry]/pets/_components/interface'
import {
  selectedAddonsAtom,
  selectedDateAtom,
  selectedDeparturesAtom,
  selectedOfferAtom,
  selectedPassengersAtom,
  selectedPetsAtom,
  selectedRoutesAtom,
  selectedVehiclesAtom,
} from '@/states/selectionAtom'

import { CommercialDiscount, getUrlLocale, isCommercialDiscount, setSessionBasket, setSessionCommercialDiscount } from '../../utils'

export const HandleRecoveredBasket = async (
  basketId: string
): Promise<{
  commercialDiscount: CommercialDiscount | null
  homeDepDate: string | null
  homeRoute: 'DVDK' | 'DKDV' | 'CADV' | 'DVCA' | 'SMSH' | 'SHSM' | 'POSH' | 'SHPO' | 'SHSP' | 'SPSH' | 'TMAL' | 'ALTM' | string | null
}> => {
  const basket = await getBasketEnriched(basketId, getUrlLocale())

  setSessionBasket(basketId)

  // The recover URL carries no commercialDiscount parameter, so the basket is the only source for it.
  const basketDiscount = basket.commercialDiscount?.toLowerCase()
  const commercialDiscount: CommercialDiscount | null = isCommercialDiscount(basketDiscount) ? basketDiscount : null
  setSessionCommercialDiscount(commercialDiscount)

  const atomStore = getDefaultStore()

  atomStore.set(selectedAddonsAtom, {
    outboundAddons: [],
    returnAddons: [],
  })

  //vehicle
  atomStore.set(selectedVehiclesAtom, {
    ...atomStore.get(selectedVehiclesAtom),
    trailerCode: basket.vehicle?.trailerTypeCode || undefined,
    vehicleCode: basket.vehicle?.vehicleTypeCode || 'NCAR',
  })

  //passenger
  const agesOfChildren = basket.passengers.map((passenger) => passenger.age).filter((age) => age != null)
  const numberOfAdults = basket.passengers.filter((p) => p.categoryCode === 'Adult').length
  const numberOfPets = basket.departures[0].pets.reduce((total, a) => total + a.quantity, 0)
  let numberOfChildren: number
  if (!agesOfChildren) numberOfChildren = 0
  else {
    numberOfChildren = agesOfChildren?.filter((a) => Number(a) > 3 && Number(a) < 16).length
  }
  let numberOfInfants: number
  if (!agesOfChildren) numberOfInfants = 0
  else {
    numberOfInfants = agesOfChildren?.filter((a) => Number(a) >= 0 && Number(a) < 4).length
  }
  atomStore.set(selectedPassengersAtom, {
    ...atomStore.get(selectedPassengersAtom),
    adults: numberOfAdults,
    children: numberOfChildren,
    childrenAges: agesOfChildren ? agesOfChildren?.map((age) => Number(age)) : [],
    infants: numberOfInfants,
  })

  if (numberOfPets && numberOfPets > 0) {
    atomStore.set(selectedPetsAtom, {
      pets: basket.departures[0].pets.map((a) => {
        const pet: SelectedPet = {
          addonCode: a.addonCode,
          addonType: a.typeCode,
          capacityInfo: {
            availableCapacity: 9999,
            capacityUnit: 'UNDEFINED',
            maxAllowedToReserve: 9999,
          },
          id: a.id,
          parentAddonCode: a.parentAddonCode,
          priceInfo: {
            basePrice: a.unitPriceStructure.basePrice,
            currency: a.unitPriceStructure.currency || basket.priceStructure.currency,
          },
          selectedQuantity: a.quantity,
        }
        return pet
      }),
      showNumberOfPets: numberOfPets,
    })
  } else {
    atomStore.set(selectedPetsAtom, {
      pets: [],
      showNumberOfPets: 0,
    })
  }

  //departures
  const depOut = basket.departures[0]
  const depHome = basket.departures[1] ?? null
  atomStore.set(selectedDateAtom, {
    ...atomStore.get(selectedDateAtom),
    outboundDepartureDate: depOut.departureDate,
    returnDepartureDate: depHome ? depHome.departureDate : null,
  })
  atomStore.set(selectedRoutesAtom, {
    homebound: depHome ? mapUnloRoute(`${depHome.departurePort}-${depHome.arrivalPort}`) : null,
    outbound: mapUnloRoute(`${depOut.departurePort}-${depOut.arrivalPort}`),
  })

  if (depOut.departureTime && depOut.id) {
    atomStore.set(selectedDeparturesAtom, {
      ...atomStore.get(selectedDeparturesAtom),
      departureHome:
        depHome && depHome.departureTime
          ? {
              arrivalDateTime: depHome.arrivalDateTime,
              arrivalPortCode: depHome.arrivalPort,
              checkinDateTime: depHome.checkinDateTime,
              currencyCode: basket.priceStructure.currency,
              departureDate: depHome.departureDate,
              departureDateTimeForBasket: depHome.departureDateTimeForBasket,
              departurePortCode: depHome.departurePort,
              departurePrice: depHome.ticketPrice,
              departureTime: depHome.departureTime,
              id: depHome.id,
              listPosition: 0,
              productCode: depHome.productCode,
            }
          : null,
      departureOut:
        depOut.departureTime && depOut.id
          ? {
              arrivalDateTime: depOut.arrivalDateTime,
              arrivalPortCode: depOut.arrivalPort,
              checkinDateTime: depOut.checkinDateTime,
              currencyCode: basket.priceStructure.currency,
              departureDate: depOut.departureDate,
              departureDateTimeForBasket: depOut.departureDateTimeForBasket,
              departurePortCode: depOut.departurePort,
              departurePrice: depOut.ticketPrice,
              departureTime: depOut.departureTime,
              id: depOut.id,
              listPosition: 0,
              productCode: depOut.productCode,
            }
          : null,
    })

    atomStore.set(selectedAddonsAtom, {
      ...atomStore.get(selectedAddonsAtom),
      outboundAddons: depOut.addons.map((a) => {
        return {
          addonCode: a.addonCode,
          addonType: a.typeCode,
          capacityInfo: {
            availableCapacity: a.quantity,
            capacityUnit: 'PASSENGER' as const,
            maxAllowedToReserve: a.quantity,
          },
          departureId: depOut.id || '',
          id: a.id,
          parentAddonCode: a.parentAddonCode,
          priceInfo: {
            basePrice: a.unitPriceStructure.basePrice,
            currency: a.unitPriceStructure.currency || basket.priceStructure.currency,
          },
          selectedQuantity: a.quantity,
        }
      }),
      returnAddons:
        depHome?.addons?.length > 0
          ? depHome.addons.map((a) => {
              return {
                addonCode: a.addonCode,
                addonType: a.typeCode,
                capacityInfo: {
                  availableCapacity: a.quantity,
                  capacityUnit: 'PASSENGER' as const,
                  maxAllowedToReserve: a.quantity,
                },
                departureId: depHome.id || '',
                id: a.id,
                parentAddonCode: a.parentAddonCode,
                priceInfo: {
                  basePrice: a.unitPriceStructure.basePrice,
                  currency: a.unitPriceStructure.currency || basket.priceStructure.currency,
                },
                selectedQuantity: a.quantity,
              }
            })
          : [],
    })

    const offerCode = basket.initialOfferCode
    if (offerCode) {
      atomStore.set(selectedOfferAtom, {
        offerCode: offerCode,
      })
    }
  }

  const urlParams = new URLSearchParams(window.location.search)
  urlParams.delete('sessionId')
  window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`)
  return {
    commercialDiscount,
    homeDepDate: depHome ? depHome.departureDate : null,
    homeRoute: depHome ? mapUnloRoute(`${depHome.departurePort}-${depHome.arrivalPort}`) : null,
  }
}
